import { yupResolver } from '@hookform/resolvers/yup'
import { TFunction } from 'i18next'
import { useEffect, useState } from 'react'
import { UseFormReturn, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { ObjectSchema, object } from 'yup'

import { AccountContract } from '@/Contract/application'
import {
  BankAccountAssociatedPhoneNumberModel,
  BankAccountRepository,
} from '@/Contract/domain'
import { LogaltyResponseStatus } from '@/Logalty/domain'
import { IUseLogaltyFirmController, useLogaltyFirmController } from '@/Logalty/ui/hooks'
import { HttpModel, WebTagPermissionEnum, documentType, textToPdf } from '@/Shared/domain'
import {
  documentNumberSchema,
  documentTypeSchema,
  ibanSchema,
  nonEmptyPositiveNumberSchema,
  nonEmptyStringSchema,
  stringSchema,
  useMetaResponseHandler,
} from '@/Shared/ui/form'
import { useLoader } from '@/Shared/ui/loader'
import { navigationRoutes } from '@/Shared/ui/routers'
import { useWebTagPermissions } from '@/User/context'

export interface BankAccountChangeForm {
  merchantTargetId: number
  newIban: string
  signerName: string
  signerSurname1: string
  signerSurname2?: string
  signerDocumentType?: documentType
  signerDocument?: string
  signerPhoneId: number
}

export interface BankAccountConfirmationForm {
  merchantTargetId: number
  operationId: string
}

const changeFormSchema = (
  t: TFunction<'translation', undefined>,
  currentIban: string,
): ObjectSchema<BankAccountChangeForm> =>
  object({
    merchantTargetId: nonEmptyPositiveNumberSchema(t),
    newIban: ibanSchema(t, currentIban),
    signerName: nonEmptyStringSchema(t, 50),
    signerSurname1: nonEmptyStringSchema(t, 50),
    signerSurname2: stringSchema(t, 50),
    signerDocumentType: documentTypeSchema(t, true),
    signerDocument: documentNumberSchema(t, true, 'signerDocumentType'),
    signerPhoneId: nonEmptyPositiveNumberSchema(t),
  })

const confirmationFormSchema = (
  t: TFunction<'translation', undefined>,
): ObjectSchema<BankAccountConfirmationForm> =>
  object({
    merchantTargetId: nonEmptyPositiveNumberSchema(t),
    operationId: nonEmptyStringSchema(t),
  })

export interface IUseBankAccountController {
  changeForm: UseFormReturn<BankAccountChangeForm>
  confirmationForm: UseFormReturn<BankAccountConfirmationForm>
  currentIban: string
  availablePhones: BankAccountAssociatedPhoneNumberModel[]
  accountChangeStep: number
  getSepaDocument: (data: BankAccountChangeForm) => Promise<void>
  downloadSepaDocument: () => Promise<void>
  isSepaDownloaded: () => boolean
  acceptSepa: (data: BankAccountConfirmationForm) => Promise<void>
  nextStep: () => void
  previousStep: () => void
  navigateToOrigin: () => void
  logalty: IUseLogaltyFirmController
}

export const useBankAccountController = (
  repository: BankAccountRepository,
  merchantId: number,
  origin = navigationRoutes.home,
): IUseBankAccountController => {
  const numberOfSteps = 4

  const { t } = useTranslation()
  const { startLoading, stopLoading } = useLoader()
  const { handleMetaResponse } = useMetaResponseHandler()
  const { hasPermission, permissions } = useWebTagPermissions();
  const navigate = useNavigate()

  const [accountChangeStep, setAccountChangeStep] = useState<number>(1)

  const [currentIban, setCurrentIban] = useState<string>('')
  const [availablePhones, setAvailablePhones] = useState<
    BankAccountAssociatedPhoneNumberModel[]
  >([])

  const getSignStatusFunction = (): Promise<HttpModel<LogaltyResponseStatus>> => {
    const data = confirmationForm.getValues()
    return AccountContract(repository).getSignStatus(data)
  }
  const logalty = useLogaltyFirmController(getSignStatusFunction)

  const [sepa, setSepa] = useState<{ file: string; downloaded: boolean }>({
    file: '',
    downloaded: false,
  })

  const isSepaDownloaded = (): boolean => sepa.downloaded

  const changeForm = useForm<BankAccountChangeForm>({
    resolver: yupResolver(changeFormSchema(t, currentIban)),
    mode: 'onChange',
    defaultValues: {
      merchantTargetId: merchantId,
      newIban: '',
      signerName: '',
      signerSurname1: '',
      signerSurname2: '',
      signerDocumentType: undefined,
      signerDocument: '',
      signerPhoneId: undefined,
    },
  })

  const confirmationForm = useForm<BankAccountConfirmationForm>({
    resolver: yupResolver(confirmationFormSchema(t)),
    mode: 'onChange',
    defaultValues: {
      merchantTargetId: merchantId,
      operationId: '',
    },
  })

  const nextStep = (): void => {
    setAccountChangeStep(Math.min(accountChangeStep + 1, numberOfSteps))
    window.scrollTo(0, 0)
  }

  const previousStep = (): void => {
    setAccountChangeStep(Math.max(accountChangeStep - 1, 1))
    window.scrollTo(0, 0)
  }

  const navigateToOrigin = (): void => {
    navigate(origin)
  }

  const getBankAccountData = async (id: number): Promise<void> => {
    startLoading()

    const response = await AccountContract(repository).getBankAccountData(id)

    stopLoading()

    if (handleMetaResponse(response?.meta, undefined, { notifySuccess: false })) {
      setCurrentIban(response?.data?.iban || '')
      setAvailablePhones(response?.data?.associatedPhoneNumbers || [])
    }
  }

  const getSepaDocument = async (data: BankAccountChangeForm): Promise<void> => {
    startLoading()

    const response = await AccountContract(repository).getSepaDocument(data)

    stopLoading()

    if (
      handleMetaResponse(response?.meta, changeForm, {
        notifySuccess: false,
      })
    ) {
      confirmationForm.setValue('operationId', response?.data?.operationId || '')
      setSepa({ file: response?.data?.sepaFileContent || '', downloaded: false })
      nextStep()
    }
  }

  const downloadSepaDocument = async (): Promise<void> => {
    startLoading()

    textToPdf(sepa.file, 'SEPA')

    setSepa({ ...sepa, downloaded: true })

    stopLoading()
  }

  const acceptSepa = async (data: BankAccountConfirmationForm): Promise<void> => {
    startLoading()

    const response = await AccountContract(repository).acceptSepaDocument(data)

    stopLoading()

    if (handleMetaResponse(response?.meta, undefined, { notifySuccess: false })) {
      if (!response?.data?.signUrl || !response?.data?.signSAML) {
        logalty.setSignStatus(LogaltyResponseStatus.Error)
        setAccountChangeStep(4)
        window.scrollTo(0, 0)
      } else {
        logalty.setLogalty({
          signUrl: response?.data?.signUrl || '',
          signSAML: atob(response?.data?.signSAML || ''),
        })
        nextStep()
      }
    }
  }

  useEffect(() => {
    if (merchantId && (hasPermission(WebTagPermissionEnum.ACTION_BANK_ACCOUNT_CHANGE) || 
    hasPermission(WebTagPermissionEnum.VIEW_SHOW_BILLLING_DATA))) {
      getBankAccountData(merchantId)
    }
  }, [merchantId, permissions])

  return {
    changeForm,
    confirmationForm,
    currentIban,
    availablePhones,
    accountChangeStep,
    getSepaDocument,
    downloadSepaDocument,
    isSepaDownloaded,
    acceptSepa,
    nextStep,
    previousStep,
    navigateToOrigin,
    logalty,
  }
}
